import React, { useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';

interface ChangePasswordProps {
  onUpdatePassword: (passwordData: { password: string; passwordVerify: string }) => Promise<void>;
  isLoading?: boolean;
  themeColor?: string;
  themeColor2?: string;
  isOpen?: boolean;
  onToggleOpen?: (open: boolean) => void;
}

interface PasswordFormData {
  password: string;
  passwordVerify: string;
}

const ChangePasswordComponent: React.FC<ChangePasswordProps> = ({
  onUpdatePassword,
  isLoading = false,
  themeColor = '#3B82F6',
  themeColor2,
  isOpen: externalIsOpen,
  onToggleOpen
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onToggleOpen || setInternalIsOpen;
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState('');

  const [formPassword, setFormPassword] = useState<PasswordFormData>({
    password: '',
    passwordVerify: '',
  });

  // White color detection function (matching AccountDeletionSection)
  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    
    return false;
  };

  // Get contrasting text color for better visibility
  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  // Simple validation logic - no side effects during render
  const isDirty = formPassword.password.length > 0 || formPassword.passwordVerify.length > 0;
  const hasMinLength = formPassword.password.length >= 8;
  const passwordsMatch = formPassword.password === formPassword.passwordVerify;
  const isValid = hasMinLength && passwordsMatch && isDirty;


  const handleSubmit = async () => {
    console.log('Password submit clicked');
    
    if (!hasMinLength) {
      setPasswordErrors('Must be >= 8 characters');
      return;
    }
    
    if (!passwordsMatch) {
      setPasswordErrors('Passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      await onUpdatePassword(formPassword);
      // Reset form on success
      setFormPassword({ password: '', passwordVerify: '' });
      setIsOpen(false);
      setPasswordErrors('');
    } catch (error) {
      console.error('Error updating password:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormPassword({ password: '', passwordVerify: '' });
    setPasswordErrors('');
  };

  const handlePasswordChange = (value: string) => {
    console.log('Password changed:', value.length);
    setFormPassword(prev => {
      const newForm = { ...prev, password: value };
      
      // Update validation if password verify field has content
      if (prev.passwordVerify.length > 0) {
        if (value.length >= 8 && value === prev.passwordVerify) {
          setPasswordErrors('');
        } else if (value.length < 8) {
          setPasswordErrors('Must be >= 8 characters');
        } else if (value !== prev.passwordVerify) {
          setPasswordErrors('Passwords do not match');
        }
      } else if (passwordErrors) {
        setPasswordErrors('');
      }
      
      return newForm;
    });
  };

  const handlePasswordVerifyChange = (value: string) => {
    console.log('Password verify changed:', value.length);
    setFormPassword(prev => {
      const newForm = { ...prev, passwordVerify: value };
      
      // Update validation immediately when both fields have content
      if (prev.password.length > 0 && value.length > 0) {
        if (prev.password.length >= 8 && prev.password === value) {
          setPasswordErrors('');
        } else if (prev.password.length < 8) {
          setPasswordErrors('Must be >= 8 characters');
        } else if (prev.password !== value) {
          setPasswordErrors('Passwords do not match');
        }
      } else if (value.length === 0 && passwordErrors) {
        setPasswordErrors('');
      }
      
      return newForm;
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 ease-in-out">
      {/* Header */}
      <div
        className="px-6 py-4 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ background: themeColor }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Lock className="w-6 h-6 mr-3" style={{ color: getContrastColor(themeColor) }} />
            <h3 
              className="text-xl font-bold"
              style={{ color: getContrastColor(themeColor) }}
            >
              Change Password
            </h3>
          </div>
          <div
            className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: getContrastColor(themeColor) }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Collapsible Content */}
      {isOpen && (
        <div className="p-6">
          <div className="space-y-6">
            {/* Password Requirements Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Password Requirements</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Must be at least 8 characters long</li>
                <li>• Both password fields must match</li>
                <li>• Choose a strong, unique password</li>
              </ul>
            </div>

            {/* New Password Field */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formPassword.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white pr-10"
                  placeholder="Enter new password"
                  disabled={isSaving || isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSaving || isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showPasswordVerify ? 'text' : 'password'}
                  value={formPassword.passwordVerify}
                  onChange={(e) => handlePasswordVerifyChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white pr-10"
                  placeholder="Confirm new password"
                  disabled={isSaving || isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPasswordVerify(!showPasswordVerify)}
                  disabled={isSaving || isLoading}
                >
                  {showPasswordVerify ? (
                    <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {passwordErrors && (
                <p className="mt-1 text-sm text-red-600">{passwordErrors}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
              {!isSaving && !isLoading ? (
                <>
                  <button
                    type="button"
                    disabled={!isDirty}
                    onClick={handleReset}
                    className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: getContrastColor(themeColor) === "#ffffff" ? "#f3f4f6" : "#374151",
                      color: getContrastColor(themeColor) === "#ffffff" ? "#374151" : "#ffffff",
                    }}
                    onMouseEnter={(e) => {
                      if (isDirty) {
                        e.currentTarget.style.backgroundColor = getContrastColor(themeColor) === "#ffffff" ? "#e5e7eb" : "#4b5563";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isDirty) {
                        e.currentTarget.style.backgroundColor = getContrastColor(themeColor) === "#ffffff" ? "#f3f4f6" : "#374151";
                      }
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    disabled={!isDirty || !isValid}
                    onClick={handleSubmit}
                    className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: (isDirty && isValid) ? themeColor : "#9CA3AF",
                      color: getContrastColor(themeColor),
                    }}
                    onMouseEnter={(e) => {
                      if (isDirty && isValid) {
                        e.currentTarget.style.backgroundColor = themeColor2 || themeColor;
                        e.currentTarget.style.color = getContrastColor(themeColor2 || themeColor);
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isDirty && isValid) {
                        e.currentTarget.style.backgroundColor = themeColor;
                        e.currentTarget.style.color = getContrastColor(themeColor);
                      }
                    }}
                  >
                    Update Password
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled
                  className="px-6 py-3 rounded-lg font-medium transition-all duration-200 cursor-not-allowed flex items-center space-x-2"
                  style={{
                    backgroundColor: themeColor,
                    color: getContrastColor(themeColor),
                  }}
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePasswordComponent;